/* ────────────────────────────────────────────────────────────
   Divine Designs · predictive-search.js
   Wires the search overlay input to Shopify Predictive Search API
   /search/suggest.json?q=…&resources[type]=product&resources[limit]=8
   ──────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var routes = (window.theme && window.theme.routes) || {};
  var DEBOUNCE = 200;
  var MIN_CHARS = 2;

  function $(s) { return document.querySelector(s); }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ── Price helpers ──────────────────────────────────────────
  // The Predictive Search API returns price as a PRE-FORMATTED money
  // string ("$36.00", "€36,00", "25.00") that already respects the shop's
  // money_format. Use it as-is for display. For numeric comparison
  // (e.g., is compare-at > current?) parse to a Number first — string
  // equality breaks across currency formats.
  function moneyToNumber(s) {
    if (s == null) return 0;
    var clean = String(s).replace(/[^\d.,]/g, '');
    if (!clean) return 0;
    // If both . and , appear, the LAST one is the decimal separator.
    // Strip the other (used as thousands).
    var lastDot = clean.lastIndexOf('.');
    var lastComma = clean.lastIndexOf(',');
    var decimalAt = Math.max(lastDot, lastComma);
    if (decimalAt > -1 && lastDot !== lastComma) {
      var thousandsChar = lastDot > lastComma ? ',' : '.';
      clean = clean.split(thousandsChar).join('');
    }
    clean = clean.replace(',', '.');
    var n = parseFloat(clean);
    return isNaN(n) ? 0 : n;
  }

  function priceHTML(p) {
    if (!p) return '';
    var min = p.price_min || p.price;
    var max = p.price_max || p.price;
    var cmpMin = p.compare_at_price_min;

    if (!min) return '';

    var current;
    if (max && min !== max) {
      current = 'From ' + escapeHTML(min);
    } else {
      current = escapeHTML(min);
    }

    var html = '<span class="price__current">' + current + '</span>';
    // Numeric comparison: only show strikethrough when compare-at > 0 AND > current.
    // Avoids the "$0.00 strikethrough next to real price" bug across currency formats.
    var cmpN = moneyToNumber(cmpMin);
    var minN = moneyToNumber(min);
    if (cmpN > 0 && cmpN > minN) {
      html = '<span class="price__strike">' + escapeHTML(cmpMin) + '</span>' + html;
    }

    if (p.available === false) {
      html += ' <span class="price__sold">· Sold out</span>';
    }

    return '<span class="price">' + html + '</span>';
  }

  function imageURL(p) {
    if (!p) return '';
    if (p.featured_image && p.featured_image.url) return p.featured_image.url;
    if (p.image) return p.image;
    if (p.images && p.images[0]) return (p.images[0].url || p.images[0]);
    return '';
  }

  function metaLine(p) {
    if (!p) return '';
    return p.product_type || p.vendor || '';
  }

  // ── API ────────────────────────────────────────────────────
  async function fetchSuggestions(q) {
    var url = (routes.predictiveSearchUrl || '/search/suggest.json')
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product'
      + '&resources[limit]=8'
      + '&resources[options][unavailable_products]=show'
      + '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
    var res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Search failed');
    var data = await res.json();
    return (data && data.resources && data.resources.results && data.resources.results.products) || [];
  }

  // ── Render ─────────────────────────────────────────────────
  function emptyState(message, sub) {
    return '<div class="sr-empty">'
         + '<p class="sr-empty__title">' + escapeHTML(message) + '</p>'
         + (sub ? '<p class="sr-empty__sub">' + sub + '</p>' : '')
         + '</div>';
  }

  function renderResultsTo(items, query, container) {
    if (!container) return;

    if (!items.length) {
      container.innerHTML = emptyState(
        'No results for "' + query + '"',
        'Try a different keyword or <a href="/collections/all">browse the full archive</a>.'
      );
      return;
    }

    container.innerHTML = items.map(function (p) {
      var img = imageURL(p);
      var meta = metaLine(p);

      return ''
        + '<a href="' + escapeHTML(p.url || '#') + '" class="sr-item" data-product-handle="' + escapeHTML(p.handle || '') + '">'
        +   '<div class="sr-item__media">'
        +     (img ? '<img src="' + escapeHTML(img) + '" alt="" loading="lazy" decoding="async" width="56" height="56">' : '')
        +   '</div>'
        +   '<div class="sr-item__info">'
        +     '<div class="sr-item__name">' + escapeHTML(p.title || '') + '</div>'
        +     (meta ? '<div class="sr-item__meta">' + escapeHTML(meta) + '</div>' : '')
        +   '</div>'
        +   '<div class="sr-item__price">' + priceHTML(p) + '</div>'
        + '</a>';
    }).join('') + (
      query
        ? '<a href="' + escapeHTML(routes.searchUrl || '/search') + '?q=' + encodeURIComponent(query) + '&type=product" class="sr-view-all">View all results for "' + escapeHTML(query) + '" →</a>'
        : ''
    );
  }

  // ── Bind one input ↔ one results container ──────────────────
  // Parameterised so the same logic powers the mobile overlay AND
  // the inline desktop search bar.
  function bindSearch(input, results) {
    if (!input || !results) return;

    var handle = debounce(async function () {
      var q = (input.value || '').trim();
      if (q.length < MIN_CHARS) {
        results.innerHTML = emptyState('Start typing to search the archive.');
        results.setAttribute('data-state', 'idle');
        return;
      }
      results.setAttribute('data-state', 'loading');
      results.innerHTML = emptyState('Searching…');
      try {
        var items = await fetchSuggestions(q);
        renderResultsTo(items, q, results);
        results.setAttribute('data-state', items.length ? 'results' : 'no-results');
      } catch (err) {
        results.innerHTML = emptyState('Search temporarily unavailable.', 'Please try again in a moment.');
        results.setAttribute('data-state', 'error');
      }
    }, DEBOUNCE);

    input.addEventListener('input', handle);
    input.addEventListener('focus', function () {
      if ((input.value || '').trim().length >= MIN_CHARS) {
        results.setAttribute('data-state', results.getAttribute('data-state') || 'results');
      }
    });

    // Escape closes the dropdown
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        results.removeAttribute('data-state');
        input.blur();
      }
    });

    // Close dropdown when clicking outside the bar or results
    document.addEventListener('click', function (e) {
      var form = input.closest('form, [role="search"]');
      var inside = (form && form.contains(e.target)) || results.contains(e.target);
      if (!inside) {
        results.removeAttribute('data-state');
      }
    });
  }

  // ── Init ───────────────────────────────────────────────────
  function init() {
    // Mobile overlay search (legacy IDs — preserve back-compat)
    bindSearch($('#search-input'), $('#search-results'));
    // Inline header search bar (new)
    bindSearch($('#header-search-input'), $('#header-search-results'));

    // Quick-tag chips inside the overlay feed the mobile input
    document.addEventListener('click', function (e) {
      var tag = e.target.closest('[data-search-term]');
      if (!tag) return;
      var i = $('#search-input');
      if (!i) return;
      i.value = tag.dataset.searchTerm;
      i.focus();
      i.dispatchEvent(new Event('input'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
